import { type TypedDocumentNode, gql } from '@apollo/client';
import { faTicket, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { animated, config, useSpring } from 'react-spring';
import styled from 'styled-components';

import { WheelType } from '@sorare/core/src/__generated__/globalTypes';
import { SilkBackground } from '@sorare/core/src/atoms/animations/SilkBackground';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Centered } from '@sorare/core/src/atoms/layout/Centered';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL } from '@sorare/core/src/atoms/typography';
import { ProbabilisticBundleReward } from '@sorare/core/src/components/3d/ProbabilisticBundleReward';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTransitionApi } from '@sorare/core/src/hooks/ui/useTransitionApi';
import { shuffle } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';
import { lazy } from '@sorare/core/src/lib/retry';

import { GoldenTickets } from './GoldenTickets';
import { Odds } from './Odds';
import { Reward } from './Reward';
import { Shape } from './Shape';
import { Slot } from './Slot';
import { Spinner } from './Spinner';
import { Tickets } from './Tickets';
import type {
  SpinWheelMutation,
  SpinWheelMutationVariables,
  WheelQuery,
  WheelQueryVariables,
} from './__generated__/index.graphql';

const Scene = lazy(
  async () => import('@sorare/trois/src/components/Scene'),
  'Scene'
);

const Wrapper = styled.div`
  display: grid;
  overflow: hidden;
  align-content: stretch;
  align-items: center;
  min-height: calc(100 * var(--vh));
  justify-items: center;
  grid-template-areas:
    'header header header'
    'wheel wheel wheel'
    'buttons buttons buttons';

  grid-template-rows: 1fr min-content 1fr;
  position: relative;
  isolation: isolate;
  & > :first-child {
    position: absolute;
    inset: 0;
    z-index: -1;
  }
`;

const Header = styled(animated.div)`
  grid-area: header;
  display: grid;
  width: 100%;
  height: 100%;
  align-content: stretch;
  align-items: stretch;

  grid-template-areas:
    'title title title'
    'free tickets golden';
  grid-template-rows: min-content min-content;
  > * {
    padding: var(--double-unit);
    border-color: var(--c-nd-200);
    border-width: 0 0 1px;
    border-style: solid;

    &:nth-child(3) {
      border-width: 0 1px 1px;
    }
  }
`;

const Footer = styled(animated(Vertical)).attrs({
  center: true,
})`
  grid-area: buttons;
`;

const query = gql`
  query WheelQuery($sport: Sport!) {
    market {
      id
      wheelCosts(sport: $sport) {
        currency
        quantity
        wheelType
      }
      wheelOdds(sport: $sport, wheelType: STANDARD) {
        id
        slots {
          probableConfigs {
            id
            ...Slot_probabilisticBundleSlotConfig
          }
        }
      }
      goldenWheelOdds: wheelOdds(sport: $sport, wheelType: GOLDEN) {
        id
        slots {
          probableConfigs {
            id
            ...Slot_probabilisticBundleSlotConfig
          }
        }
      }
    }
  }
  ${Slot.fragments.ProbabilisticBundleSlotConfig}
` as TypedDocumentNode<WheelQuery, WheelQueryVariables>;

const spinWheelMutation = gql`
  mutation SpinWheelMutation($sport: Sport!, $wheelType: WheelType!) {
    spinWheel(input: { sport: $sport, wheelType: $wheelType }) {
      errors {
        code
        message
        path
      }
      reward {
        id
        drawnSlotConfigIds
        items {
          id
          ...ProbabilisticBundleReward_probabilisticBundleSlotItemInterface
        }
      }
    }
  }
  ${ProbabilisticBundleReward.fragments.probabilisticBundleSlotItemInterface}
` as TypedDocumentNode<SpinWheelMutation, SpinWheelMutationVariables>;

export const Wheel = () => {
  const { currentUser } = useCurrentUserContext();
  const nbTickets = currentUser?.wheelTickets[0].amount ?? 0;
  const nbGoldenTickets = currentUser?.goldenWheelTickets[0].amount ?? 0;
  const [showOdds, setShowOdds] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const close = useCallback(() => setShowOdds(false), [setShowOdds]);
  const { sport } = useSportContext();
  const [stopAtIndex, setStopAtIndex] = useState<number | undefined>(undefined);
  const { data } = useQuery(query, {
    variables: {
      sport,
    },
  });
  const goldenWheelCost =
    (data?.market?.wheelCosts ?? []).find(x => x.wheelType === WheelType.GOLDEN)
      ?.quantity ?? 10;

  const isGoldenWheel = nbGoldenTickets >= goldenWheelCost;

  const shuffledProbableConfigs = useMemo(
    () =>
      shuffle(
        (isGoldenWheel
          ? data?.market?.goldenWheelOdds?.slots?.[0]?.probableConfigs
          : data?.market?.wheelOdds?.slots?.[0]?.probableConfigs) ?? []
      ) ?? [],
    [data, isGoldenWheel]
  );

  const [spinWheel] = useMutation(spinWheelMutation);
  const navigate = useNavigate();
  const [spinning, setSpinning] = useState(false);

  const [isRewardShown, setIsRewardShown] = useState(false);
  const [showUI, setShowUI] = useState(true);

  const { updateDOM } = useTransitionApi();
  const toggleReward = useCallback(
    (value: boolean) => {
      if (value === false) {
        setShowUI(true);
      }
      updateDOM(() => {
        setIsRewardShown(value);
      });
    },
    [updateDOM]
  );

  const [reward, setReward] = useState<
    | {
        reward: NonNullable<
          NonNullable<SpinWheelMutation['spinWheel']>['reward']
        >['items'][number];
        index: number;
      }
    | undefined
  >(undefined);

  const uiSpring = useSpring({
    up: showUI ? 0 : -100,
    down: showUI ? 0 : 100,
    opacity: showUI ? 1 : 0,
    config: config.gentle,
  });

  const spin = useCallback(async () => {
    setSpinning(true);
    setReward(undefined);
    toggleReward(false);
    setShowUI(false);
    const [{ data: spinWheelData }] = await Promise.all([
      spinWheel({
        variables: {
          sport,
          wheelType: isGoldenWheel ? WheelType.GOLDEN : WheelType.STANDARD,
        },
      }),
      // minimum delay
      new Promise(resolve => setTimeout(resolve, 1000)),
    ]);

    if (!spinWheelData?.spinWheel?.reward?.items?.length) {
      throw new Error('Error spinning wheel');
    }
    const index = shuffledProbableConfigs.findIndex(x =>
      spinWheelData?.spinWheel?.reward?.drawnSlotConfigIds.includes(x.id)
    );

    if (index === -1) {
      throw new Error('Cannot match slot to reward');
    }

    setReward({
      reward: spinWheelData?.spinWheel?.reward?.items[0],
      index,
    });

    return index;
  }, [toggleReward, spinWheel, sport, isGoldenWheel, shuffledProbableConfigs]);

  return (
    <Wrapper>
      <SilkBackground
        color={isGoldenWheel ? '#ffd814' : '#2b00ab'}
        fallback={null}
        key={isGoldenWheel ? 'golden' : 'standard'}
      />

      <Header style={{ y: uiSpring.up, opacity: uiSpring.opacity }}>
        <Centered
          style={{
            gridArea: 'title',
            justifySelf: 'stretch',
            padding: 'var(--double-unit)',
          }}
          right={
            <CloseButton
              onClose={() => {
                navigate(-1);
              }}
              size="small"
            />
          }
        >
          <BrandL>
            <FormattedMessage
              id="marketplace.wheel.title"
              defaultMessage="Sorare Wheel"
            />
          </BrandL>
        </Centered>

        <div>Free ticket</div>
        <Tickets nb={nbTickets} />
        <GoldenTickets nb={nbGoldenTickets} cost={goldenWheelCost} />
      </Header>

      <div
        style={{
          gridArea: 'wheel',
          alignSelf: 'stretch',
          justifySelf: 'stretch',
        }}
      >
        <Spinner
          spin={spinning}
          stopAtIndex={stopAtIndex}
          onSpinEnd={() => {
            setSpinning(false);
            toggleReward(true);
            setStopAtIndex(undefined);
          }}
        >
          {shuffledProbableConfigs.map((probableConfig, index) => (
            <Slot
              big
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              config={probableConfig}
              style={
                index === stopAtIndex && !isRewardShown
                  ? { viewTransitionName: 'wheel-reward' }
                  : {}
              }
            />
          ))}
        </Spinner>
      </div>

      <Footer style={{ y: uiSpring.down, opacity: uiSpring.opacity }}>
        <LoadingButton
          size="large"
          color="purple"
          onClick={() => {
            setSpinning(true);
            spin().then(setStopAtIndex);
          }}
          loading={spinning}
          disabled={spinning || nbTickets === 0}
        >
          <FormattedMessage id="marketplace.wheel.spin" defaultMessage="Spin" />{' '}
          <span style={{ opacity: 0.5 }}>·</span> 1{' '}
          <FontAwesomeIcon icon={faTimes} size="sm" />{' '}
          <FontAwesomeIcon icon={faTicket} />
        </LoadingButton>

        <Button
          color="tertiary"
          size="large"
          onClick={() => {
            setShowOdds(true);
            setHasOpened(true);
          }}
        >
          <FormattedMessage {...glossary.rewards} />
        </Button>
      </Footer>

      {!!isRewardShown && !!reward && (
        <Reward
          onClose={() => toggleReward(false)}
          style={{ viewTransitionName: 'wheel-reward' }}
        >
          <Shape config={shuffledProbableConfigs[reward.index]}>
            <Vertical style={{ height: '400px' }}>
              <Scene fallback={null}>
                <ProbabilisticBundleReward item={reward.reward} />
              </Scene>
            </Vertical>
          </Shape>
        </Reward>
      )}

      <Drawer
        open={showOdds}
        onBackdropClick={close}
        onClose={close}
        keepMounted={hasOpened}
      >
        <Odds onClose={close} golden={isGoldenWheel} />
      </Drawer>
    </Wrapper>
  );
};
